import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

interface OTAEnabledToggleProps {
  apiBaseUrl: string;
}

const OTAEnabledToggle: React.FC<OTAEnabledToggleProps> = ({ apiBaseUrl }) => {
  const [otaEnabled, setOtaEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the current setting when component mounts
  useEffect(() => {
    fetchSetting();
  }, []);

  const fetchSetting = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/settings/ota_enabled`, {
        withCredentials: true
      });
      
      setOtaEnabled(response.data.value);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching OTA enabled setting:', error);
      // If setting doesn't exist yet, assume it's false
      if (error.response?.status === 404) {
        setOtaEnabled(false);
      } else {
        setError('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleOTAEnabled = async () => {
    setUpdating(true);
    try {
      const response = await axios.put(
        `${apiBaseUrl}/settings/ota_enabled`,
        {
          value: !otaEnabled,
          description: 'Enable OTA updates for devices'
        },
        { withCredentials: true }
      );
      
      setOtaEnabled(response.data.value);
      setError(null);
    } catch (error: any) {
      console.error('Error updating OTA enabled setting:', error);
      setError('Failed to update setting');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="d-flex align-items-center">
      <div 
        className="form-check form-switch" 
        style={{ 
          position: 'relative',
          marginRight: '12px',
          height: '1.5em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <style>
          {`
            #otaEnabledToggle {
              background-color: #dc3545 !important;
              border-color: #dc3545 !important;
              position: relative;
              z-index: 1;
            }
            #otaEnabledToggle:checked {
              background-color: #198754 !important;
              border-color: #198754 !important;
            }
            #otaEnabledToggle::after {
              background-color: white !important;
            }
            #otaEnabledToggle {
              --bs-form-switch-bg: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='white'/%3e%3c/svg%3e") !important;
            }
          `}
        </style>
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={otaEnabled}
          onChange={toggleOTAEnabled}
          id="otaEnabledToggle"
          disabled={updating || loading}
          style={{ 
            width: '3em',
            height: '1.5em',
            margin: 0,
            opacity: (loading || updating) ? 0 : 1,
            pointerEvents: (loading || updating) ? 'none' : 'auto',
            cursor: 'pointer'
          }}
        />
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}>
            <span 
              className="spinner-border spinner-border-sm" 
              role="status" 
              aria-hidden="true"
              style={{ 
                width: '1em',
                height: '1em',
                borderWidth: '0.15em',
                marginLeft: '2.5em'
              }}
            ></span>
          </div>
        )}
        {updating && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}>
            <FontAwesomeIcon 
              icon={faSync} 
              spin 
              style={{ 
                fontSize: '1em',
                marginLeft: '2.5em'
              }}
            />
          </div>
        )}
      </div>
      <label 
        className="form-check-label" 
        htmlFor="otaEnabledToggle"
        style={{
          fontSize: '1.1rem',
          fontWeight: 500,
          margin: 0
        }}
      >
        OTA Enabled
      </label>
      
      {error && (
        <div className="text-danger ms-2 small">
          {error}
        </div>
      )}
    </div>
  );
};

export default OTAEnabledToggle; 